import { OrgScheduleState, OrgScheduleAction, ActionType } from './types'

export const createInitialState = (orgId: string): OrgScheduleState => ({
  // Organization ID
  orgId,

  // Sub Organizations
  selectedSubOrg: null,
  subOrganizations: [],
  subOrgsPagination: null,
  subOrgsSearchQuery: '',
  appendingSubOrgs: false,

  // Filter selections
  selectedSchedule: null,
  selectedTeam: null,
  selectedVenue: null,
  selectedFacility: null,
  selectedDateRange: null,
  selectedTimePeriod: 'TODAY',
  selectedSortOrder: 'asc',

  // Filter options
  schedules: [],
  teams: [],
  venues: [],

  // Filter pagination
  schedulesPagination: null,
  teamsPagination: null,
  venuesPagination: null,

  // Filter search queries
  schedulesSearchQuery: '',
  teamsSearchQuery: '',
  venuesSearchQuery: '',

  // Filter appending states
  appendingSchedules: false,
  appendingTeams: false,
  appendingVenues: false,

  // Games data
  games: [],
  gamesPagination: null,

  // Dates data
  dates: [],
  hasTodayGames: false,
  hasUpcomingGames: false,
  datesLoaded: false,

  // Loading states
  loadingGames: false,
  loadingSchedules: false,
  loadingTeams: false,
  loadingVenues: false,
  loadingSubOrgs: false,
  loadingDates: false,
  filtersLoaded: false,
})

export const orgScheduleReducer = (
  state: OrgScheduleState,
  action: OrgScheduleAction
): OrgScheduleState => {
  switch (action.type) {
    case ActionType.SET_ORG_ID:
      return {
        ...state,
        orgId: action.payload,
      }

    case ActionType.SET_SELECTED_SCHEDULE:
      return {
        ...state,
        selectedSchedule: action.payload,
      }

    case ActionType.SET_SELECTED_TEAM:
      return {
        ...state,
        selectedTeam: action.payload,
      }

    case ActionType.SET_SELECTED_VENUE:
      return {
        ...state,
        selectedVenue: action.payload,
        // Clear facility when venue changes (new venue may have different facilities)
        selectedFacility: null,
      }

    case ActionType.SET_SELECTED_FACILITY:
      return {
        ...state,
        selectedFacility: action.payload,
      }

    case ActionType.SET_SELECTED_DATE_RANGE:
      return {
        ...state,
        selectedDateRange: action.payload,
        // When date range is set, clear time period
        selectedTimePeriod: action.payload ? 'TODAY' : state.selectedTimePeriod,
      }

    case ActionType.SET_SELECTED_TIME_PERIOD:
      return {
        ...state,
        selectedTimePeriod: action.payload,
        // When time period is set, clear date range
        selectedDateRange: null,
        // Set default sort: PAST → desc, TODAY/UPCOMING → asc
        selectedSortOrder: action.payload === 'PAST' ? 'desc' : 'asc',
      }

    case ActionType.SET_SCHEDULES:
      return {
        ...state,
        schedules: action.payload.data,
        schedulesPagination: action.payload.pagination,
      }

    case ActionType.SET_TEAMS:
      return {
        ...state,
        teams: action.payload.data,
        teamsPagination: action.payload.pagination,
      }

    case ActionType.SET_VENUES:
      return {
        ...state,
        venues: action.payload.data,
        venuesPagination: action.payload.pagination,
      }

    case ActionType.SET_GAMES:
      return {
        ...state,
        games: action.payload.games,
        gamesPagination: action.payload.pagination,
      }

    case ActionType.APPEND_GAMES:
      return {
        ...state,
        games: [...state.games, ...action.payload.games],
        gamesPagination: action.payload.pagination,
      }

    case ActionType.SET_LOADING_GAMES:
      return {
        ...state,
        loadingGames: action.payload,
      }

    case ActionType.SET_LOADING_SCHEDULES:
      return {
        ...state,
        loadingSchedules: action.payload,
      }

    case ActionType.SET_LOADING_TEAMS:
      return {
        ...state,
        loadingTeams: action.payload,
      }

    case ActionType.SET_LOADING_VENUES:
      return {
        ...state,
        loadingVenues: action.payload,
      }

    case ActionType.SET_LOADING_DATES:
      return {
        ...state,
        loadingDates: action.payload,
      }

    case ActionType.SET_DATES: {
      const defaultPeriod =
        action.payload.defaultTimePeriod ?? state.selectedTimePeriod
      return {
        ...state,
        dates: action.payload.dates,
        hasTodayGames: action.payload.hasTodayGames,
        hasUpcomingGames: action.payload.hasUpcomingGames,
        datesLoaded: true,
        selectedTimePeriod: defaultPeriod,
        selectedSortOrder: defaultPeriod === 'PAST' ? 'desc' : 'asc',
      }
    }

    case ActionType.CLEAR_FILTER: {
      const updates: Partial<OrgScheduleState> = {}

      switch (action.payload) {
        case 'schedule':
          updates.selectedSchedule = null
          break
        case 'team':
          updates.selectedTeam = null
          break
        case 'venue':
          updates.selectedVenue = null
          updates.selectedFacility = null
          break
        case 'facility':
          updates.selectedFacility = null
          break
        case 'dateRange':
          updates.selectedDateRange = null
          break
        case 'timePeriod':
          updates.selectedTimePeriod = 'TODAY'
          break
        case 'subOrg':
          updates.selectedSubOrg = null
          // Clear dependent filters when sub org is cleared
          updates.selectedSchedule = null
          updates.selectedTeam = null
          updates.selectedVenue = null
          updates.selectedFacility = null
          break
      }

      return {
        ...state,
        ...updates,
      }
    }

    case ActionType.CLEAR_ALL_FILTERS:
      return {
        ...state,
        selectedSchedule: null,
        selectedTeam: null,
        selectedVenue: null,
        selectedFacility: null,
        selectedDateRange: null,
        selectedTimePeriod: 'TODAY',
      }

    case ActionType.APPEND_SCHEDULES:
      return {
        ...state,
        schedules: [...state.schedules, ...action.payload.data],
        schedulesPagination: action.payload.pagination,
      }

    case ActionType.APPEND_TEAMS:
      return {
        ...state,
        teams: [...state.teams, ...action.payload.data],
        teamsPagination: action.payload.pagination,
      }

    case ActionType.SET_APPENDING_SCHEDULES:
      return {
        ...state,
        appendingSchedules: action.payload,
      }

    case ActionType.SET_APPENDING_TEAMS:
      return {
        ...state,
        appendingTeams: action.payload,
      }

    case ActionType.SET_SCHEDULES_SEARCH_QUERY:
      return {
        ...state,
        schedulesSearchQuery: action.payload,
      }

    case ActionType.SET_TEAMS_SEARCH_QUERY:
      return {
        ...state,
        teamsSearchQuery: action.payload,
      }

    case ActionType.APPEND_VENUES:
      return {
        ...state,
        venues: [...state.venues, ...action.payload.data],
        venuesPagination: action.payload.pagination,
      }

    case ActionType.SET_APPENDING_VENUES:
      return {
        ...state,
        appendingVenues: action.payload,
      }

    case ActionType.SET_VENUES_SEARCH_QUERY:
      return {
        ...state,
        venuesSearchQuery: action.payload,
      }

    case ActionType.SET_SELECTED_SORT_ORDER:
      return {
        ...state,
        selectedSortOrder: action.payload,
      }

    case ActionType.SET_FILTERS_LOADED:
      return {
        ...state,
        filtersLoaded: action.payload,
      }

    // Sub Organizations actions
    case ActionType.SET_SELECTED_SUB_ORG:
      return {
        ...state,
        selectedSubOrg: action.payload,
        // Clear dependent filters when sub org changes
        selectedSchedule: null,
        selectedTeam: null,
        selectedVenue: null,
        selectedFacility: null,
        // Clear filter options (will be refetched)
        schedules: [],
        teams: [],
        venues: [],
        schedulesPagination: null,
        teamsPagination: null,
        venuesPagination: null,
      }

    case ActionType.SET_SUB_ORGANIZATIONS:
      return {
        ...state,
        subOrganizations: action.payload.data,
        subOrgsPagination: action.payload.pagination,
      }

    case ActionType.APPEND_SUB_ORGANIZATIONS:
      return {
        ...state,
        subOrganizations: [...state.subOrganizations, ...action.payload.data],
        subOrgsPagination: action.payload.pagination,
      }

    case ActionType.SET_LOADING_SUB_ORGS:
      return {
        ...state,
        loadingSubOrgs: action.payload,
      }

    case ActionType.SET_APPENDING_SUB_ORGS:
      return {
        ...state,
        appendingSubOrgs: action.payload,
      }

    case ActionType.SET_SUB_ORGS_SEARCH_QUERY:
      return {
        ...state,
        subOrgsSearchQuery: action.payload,
      }

    default:
      return state
  }
}
